/**
 * Enquiry / message triage (Cloudflare Clef via the triage-contacts function).
 * Stored in the staff-only contact_triage table — one row per lead or thread.
 * Effective value = staff override when set, else the AI value.
 * Keep keys in sync with the contact_triage CHECK constraint and
 * supabase/functions/triage-contacts/index.ts.
 */

export const TRIAGE_CATEGORIES = {
  apply_ready:      { label: "Ready to apply",          short: "Apply",       color: "bg-emerald-100 text-emerald-800 border-emerald-200", route: "Sales — help choose a package and start the application." },
  info_request:     { label: "Information request",     short: "Info",        color: "bg-sky-100 text-sky-800 border-sky-200",             route: "Sales — answer questions, qualify budget and timing." },
  application_help: { label: "Application in progress", short: "Application", color: "bg-blue-100 text-blue-800 border-blue-200",          route: "Applications team — check the application and reply." },
  existing_member:  { label: "Existing member",         short: "Member",      color: "bg-amber-100 text-amber-800 border-amber-200",       route: "Member services — verify membership, link the account." },
  member_service:   { label: "Member service",          short: "Service",     color: "bg-violet-100 text-violet-800 border-violet-200",    route: "Member services — handle the service request." },
  issue:            { label: "Issue / complaint",       short: "Issue",       color: "bg-red-100 text-red-800 border-red-200",             route: "Senior staff — resolve quickly and personally." },
  partner:          { label: "Partner / agency",        short: "Partner",     color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200", route: "Partnerships — route to partner onboarding." },
  other_visa:       { label: "Other visa",              short: "Other visa",  color: "bg-slate-100 text-slate-700 border-slate-200",       route: "Qualify, then refer to the right visa partner." },
  unrelated:        { label: "Unrelated / job",         short: "Unrelated",   color: "bg-stone-100 text-stone-600 border-stone-200",       route: "Job application or off-topic — reply briefly or close; disable the account if abusive." },
  spam:             { label: "Spam",                    short: "Spam",        color: "bg-gray-100 text-gray-500 border-gray-200",          route: "No action — close or mark as spam." },
} as const;

export type TriageCategory = keyof typeof TRIAGE_CATEGORIES;

export const PRIORITIES: Record<number, { label: string; color: string; dot: string }> = {
  4: { label: "Urgent", color: "bg-red-600 text-white border-red-700",       dot: "bg-red-600" },
  3: { label: "High",   color: "bg-orange-500 text-white border-orange-600", dot: "bg-orange-500" },
  2: { label: "Normal", color: "bg-blue-100 text-blue-800 border-blue-200",  dot: "bg-blue-500" },
  1: { label: "Low",    color: "bg-gray-100 text-gray-600 border-gray-200",  dot: "bg-gray-400" },
};

export interface TriageRow {
  id?: string;
  lead_id?: string | null;
  thread_id?: string | null;
  category: string | null;
  priority: number | null;
  confidence?: number | null;
  signals?: Record<string, { noul?: number; choice?: string; score?: number; confidence?: number }> | null;
  triaged_at?: string | null;
  override_category: string | null;
  override_priority: number | null;
  overridden_at?: string | null;
}

export const TRIAGE_COLUMNS =
  "id, lead_id, thread_id, category, priority, confidence, signals, triaged_at, override_category, override_priority, overridden_at";

/** PostgREST embed for threads (contact_triage.thread_id → threads.id). */
export const THREAD_TRIAGE_EMBED = `contact_triage(${TRIAGE_COLUMNS})`;

export function oneTriage(raw: unknown): TriageRow | null {
  const row = Array.isArray(raw) ? raw[0] : raw;
  return (row as TriageRow) ?? null;
}

export function effectiveTriage(row: TriageRow | null | undefined): {
  category: TriageCategory | null; priority: number | null; overridden: boolean; aiOnly: boolean;
} {
  if (!row) return { category: null, priority: null, overridden: false, aiOnly: false };
  const category = (row.override_category ?? row.category) as TriageCategory | null;
  const priority = row.override_priority ?? row.priority;
  const overridden = row.override_category != null || row.override_priority != null;
  return { category: category && category in TRIAGE_CATEGORIES ? category : null, priority, overridden, aiOnly: !overridden && row.triaged_at != null };
}

/** Human-readable AI signals for the hover card. */
export function triageSignals(row: TriageRow | null | undefined): string[] {
  const s = row?.signals;
  if (!s) return [];
  const pct = (v?: number) => (v == null ? "?" : `${Math.round(v * 100)}%`);
  const out: string[] = [];
  if (s.genuine?.noul != null) out.push(`Genuine: ${pct(s.genuine.noul)}`);
  if (s.wants_to_apply?.noul != null) out.push(`Wants to apply: ${pct(s.wants_to_apply.noul)}`);
  if (s.needs_reply_today?.noul != null) out.push(`Needs reply today: ${pct(s.needs_reply_today.noul)}`);
  if (s.is_existing_member?.noul != null) out.push(`Existing member: ${pct(s.is_existing_member.noul)}`);
  if (s.category?.confidence != null) out.push(`Type confidence: ${pct(s.category.confidence)}`);
  return out;
}

/** Profile / lead tags that mean "staff should review and maybe disable". */
export const FLAG_TAGS: Record<string, string> = {
  "flagged:inappropriate-upload": "Inappropriate upload",
  "flagged:spam": "Spam (AI)",
  "flagged:unrelated": "Unrelated / job (AI)",
};
export const flagTagsOf = (tags: string[] | null | undefined) => (tags ?? []).filter((t) => t.startsWith("flagged:"));

/** Pipeline stage shown on conversations: application status, else contact stage. */
export function pipelineStageLabel(opts: { applicationStatus?: string | null; leadStatus?: string | null; isMember?: boolean; hasLead?: boolean }): string {
  if (opts.applicationStatus) return opts.applicationStatus.replace(/_/g, " ");
  if (opts.isMember) return "member";
  if (opts.leadStatus) return `lead · ${opts.leadStatus.replace(/_/g, " ")}`;
  return opts.hasLead ? "lead" : "registered";
}
